import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { getAuthToken } from '../auth.interceptor';
import { createEmbeddingContext, EmbeddingContext } from 'amazon-quicksight-embedding-sdk';

@Injectable({
    providedIn: 'root',
})
export class QuickSightChatService {
    private embeddingContext: EmbeddingContext | null = null;
    private chatExperience: any = null;
    private staticContainer: HTMLElement | null = null;
    private initialized = false;

    constructor(
        private http: HttpClient,
        @Inject(APP_CONFIG) private config: AppConfig
    ) { }

    /**
     * Fetches an embed URL from the backend API.
     *
     */
    async getEmbedUrl(): Promise<{ embedUrl: string; requiresManualSignIn: boolean }> {
        const token = getAuthToken();
        let headers = new HttpHeaders({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        });

        // Attach the API Gateway key when configured
        if (this.config.quickSightChat.apiKey) {
            headers = headers.set('x-api-key', this.config.quickSightChat.apiKey);
        }


        const response = await firstValueFrom(
            this.http.get<{ embedUrl: string; requiresManualSignIn?: boolean }>(
                this.config.quickSightChat.embedUrlApi,
                { headers }
            )
        );
        return {
            embedUrl: response.embedUrl,
            requiresManualSignIn: response.requiresManualSignIn === true,
        };

    }

    /**
     * Embeds the QuickSight chat agent into the specified container element.
     * Uses the QuickSight Embedding SDK for a seamless, SSO-authenticated experience.
     */
    async embedChat(container: HTMLElement): Promise<void> {
        if (this.initialized) {
            // If already initialized, destroy and re-embed
            this.destroy();
        }

        try {
            const { embedUrl, requiresManualSignIn } = await this.getEmbedUrl();

            // No matching QuickSight user: the backend returned a static share
            // URL.
            if (requiresManualSignIn) {
                this.embedManualSignIn(container, embedUrl);
                return;
            }

            this.embeddingContext = await createEmbeddingContext();

            const agentArn = `arn:aws:quicksight:${this.config.quickSightChat.region}:${this.config.quickSightChat.accountId}:agent/${this.config.quickSightChat.agentId}`;

            const frameOptions = {
                url: embedUrl,
                container,
                height: '100%',
                width: '100%',
                className: 'quicksight-chat-iframe',
                withIframePlaceholder: true,
                framePermissions: {
                    clipboardRead: true,
                    clipboardWrite: true,
                },
                onChange: (changeEvent: any) => {
                    switch (changeEvent.eventName) {
                        case 'FRAME_MOUNTED':
                            console.log('[QuickSight Chat] Frame mounted');
                            break;
                        case 'FRAME_LOADED':
                            console.log('[QuickSight Chat] Frame loaded');
                            break;
                    }
                },
            };

            const contentOptions = {
                fixedAgentArn: agentArn,
                footerOptions: {
                    showBrandAttribution: false,
                    showUsagePolicy: false,
                },
                promptOptions: {
                    showChatHistory: true,
                    allowFileAttachments: true,
                },
                onMessage: async (messageEvent: any) => {
                    switch (messageEvent.eventName) {
                        case 'CONTENT_LOADED':
                            console.log('[QuickSight Chat] Content loaded successfully');
                            break;
                        case 'ERROR_OCCURRED':
                            console.error('[QuickSight Chat] Error:', messageEvent);
                            break;
                    }
                },
            };

            this.chatExperience = await this.embeddingContext.embedQuickChat(frameOptions, contentOptions);
            this.initialized = true;
        } catch (error) {
            console.error('[QuickSight Chat] Failed to embed chat:', error);
            throw error;
        }
    }

    /**
     * Renders the static QuickSight share URL in a plain iframe.
     *
     * Used for the manual-sign-in fallback: the Okta token is valid but has no
     * matching QuickSight user, so the backend returns a static share URL with
     * requiresManualSignIn=true.
     */
    private embedManualSignIn(container: HTMLElement, url: string): void {
        // Clear any previous content.
        container.innerHTML = '';

        const iframe = document.createElement('iframe');
        iframe.src = url;
        iframe.className = 'quicksight-chat-iframe';
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.border = 'none';
        // Delegate clipboard access to the QuickSight origin, matching the
        // permissions the embedding SDK grants on the registered-user path.
        const qsOrigin = `https://${this.config.quickSightChat.region}.quicksight.aws.amazon.com`;
        iframe.setAttribute('allow', `clipboard-read ${qsOrigin}; clipboard-write ${qsOrigin}`);

        container.appendChild(iframe);
        this.staticContainer = container;
        this.initialized = true;
        console.log('[QuickSight Chat] Rendered static share URL in iframe (manual sign-in)');
    }

    /**
     * Send a prompt to the embedded chat programmatically.
     */
    async sendPrompt(prompt: string): Promise<void> {
        if (this.chatExperience) {
            await this.chatExperience.sendPrompt(prompt);
        }
    }

    /**
     * Clean up the embedded chat experience.
     */
    destroy(): void {
        // Clear the manual sign-in prompt if one was rendered.
        if (this.staticContainer) {
            this.staticContainer.innerHTML = '';
            this.staticContainer = null;
        }
        this.chatExperience = null;
        this.embeddingContext = null;
        this.initialized = false;
    }

    /**
     * Whether the chat is currently embedded and active.
     */
    get isActive(): boolean {
        return this.initialized;
    }
}
